import { z } from 'zod';
import { DisciplineSchema, OfficeSlugSchema } from './common';

export const OPPORTUNITY_STATUSES = ['active', 'expired', 'archived'] as const;
export const OpportunityStatusSchema = z.enum(OPPORTUNITY_STATUSES);
export type OpportunityStatus = z.infer<typeof OpportunityStatusSchema>;

export const OPPORTUNITY_ACTIONS = [
  'pursue', 'pass', 'estimated', 'submitted', 'won', 'lost', 'abandoned',
] as const;
export const OpportunityActionSchema = z.enum(OPPORTUNITY_ACTIONS);
export type OpportunityAction = z.infer<typeof OpportunityActionSchema>;

export const SOURCE_TYPES = [
  'sam_gov_api', 'planetbids', 'opengov', 'custom', 'email_forward',
  // Phase 44 — GC bid-list mining. Sources with source_type='gc_awards' route
  // to the gc-award-ingestion queue + gc_awards table (NOT opportunities).
  'gc_awards',
] as const;
export const SourceTypeSchema = z.enum(SOURCE_TYPES);
export type SourceType = z.infer<typeof SourceTypeSchema>;

export const OpportunitySchema = z.object({
  id: z.string(),
  sourceId: z.string(),
  externalId: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  bidDueAt: z.string().nullable(),
  estimatedValueMin: z.number().nullable(),
  estimatedValueMax: z.number().nullable(),
  disciplines: z.array(DisciplineSchema),
  locationCity: z.string().nullable(),
  locationState: z.string().nullable(),
  locationLat: z.number().nullable(),
  locationLng: z.number().nullable(),
  distanceToRanchoMi: z.number().nullable(),
  distanceToLivermoreMi: z.number().nullable(),
  distanceToSparksMi: z.number().nullable(),
  nearestOffice: OfficeSlugSchema.nullable(),
  nearestOfficeMi: z.number().nullable(),
  gcName: z.string().nullable(),
  ownerName: z.string().nullable(),
  projectType: z.string().nullable(),
  planSetUrl: z.string().nullable(),
  sourcePageUrl: z.string().url(),
  firstSeenAt: z.string(),
  lastSeenAt: z.string(),
  status: OpportunityStatusSchema,
});
export type Opportunity = z.infer<typeof OpportunitySchema>;

export const OpportunityRankSchema = z.object({
  opportunityId: z.string(),
  score: z.number().int().min(0).max(100),
  disciplineMatchCount: z.number().int().nonnegative(),
  inValueBand: z.boolean(),
  distanceScore: z.number().min(0).max(1),
  explanationJson: z.string().nullable(),
  computedAt: z.string(),
});
export type OpportunityRank = z.infer<typeof OpportunityRankSchema>;

// disciplines may arrive as a single string (?disciplines=CCTV) or as
// repeated params (?disciplines=CCTV&disciplines=FIRE_ALARM). Normalize both
// shapes to string[].
const disciplinesParam = z.preprocess(
  (v) => (v == null ? undefined : Array.isArray(v) ? v : [v]),
  z.array(DisciplineSchema).optional(),
);

// Filter status — accept the canonical statuses + a few feed shortcuts that
// the Opportunities tab uses for its "what to look at right now" tabs.
//   active        — default. status='active'
//   expired       — status='expired' (or active rows past bid_due_at)
//   archived      — status='archived'
//   new_today     — status='active' AND first_seen_at >= now() - 24h
//   updated_today — status='active' AND last_seen_at  >= now() - 24h AND not new
//   closing_soon  — status='active' AND bid_due_at within `closingDays` (default 7)
export const FeedStatusSchema = z.enum([
  'active', 'expired', 'archived', 'new_today', 'updated_today', 'closing_soon',
]);
export type FeedStatus = z.infer<typeof FeedStatusSchema>;

export const OpportunityFeedQuerySchema = z.object({
  minScore: z.coerce.number().int().min(0).max(100).optional(),
  maxDistance: z.coerce.number().positive().max(5000).optional(),
  disciplines: disciplinesParam,
  valueMin: z.coerce.number().nonnegative().optional(),
  valueMax: z.coerce.number().positive().optional(),
  bidDueBefore: z.string().datetime().optional(),
  nearestOffice: OfficeSlugSchema.optional(),
  hasAction: OpportunityActionSchema.optional(),
  search: z.string().max(128).optional(),
  // New 2026-05-07: filter by source ID + feed-status preset.
  sourceId: z.string().max(64).optional(),
  status: FeedStatusSchema.optional(),
  closingDays: z.coerce.number().int().min(1).max(60).optional(),
  // Phase 37F: default 'relevant' shows only opps with at least one signal
  // (discipline match / customer match / PURSUE vertical / authorized mfg
  // / score >= 30 / has user action). 'all' shows everything including
  // zero-signal noise. The hygiene cron archives most zero-signal noise
  // anyway, so 'all' is mostly useful for debugging.
  relevance: z.enum(['relevant', 'all']).default('relevant'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
});
export type OpportunityFeedQuery = z.infer<typeof OpportunityFeedQuerySchema>;

export const CreateOpportunityActionSchema = z.object({
  action: OpportunityActionSchema,
  notes: z.string().max(2048).optional(),
  bidAmount: z.number().positive().optional(),
  wonAmount: z.number().positive().optional(),
  projectId: z.string().optional(),
});
export type CreateOpportunityAction = z.infer<typeof CreateOpportunityActionSchema>;

export const OpportunitySourceSchema = z.object({
  id: z.string(),
  name: z.string(),
  url: z.string().url(),
  sourceType: SourceTypeSchema,
  crawlConfig: z.record(z.unknown()),
  cadenceMinutes: z.number().int().positive(),
  enabled: z.boolean(),
  lastCrawledAt: z.string().nullable(),
  lastSuccessAt: z.string().nullable(),
  lastError: z.string().nullable(),
  totalCrawls: z.number().int().nonnegative(),
  totalOpportunitiesFound: z.number().int().nonnegative(),
  notes: z.string().nullable(),
});
export type OpportunitySource = z.infer<typeof OpportunitySourceSchema>;

// Scraper extraction schemas. Phase 36L gates source-ingestion on this:
// every candidate runs through `safeParse` before persistence; rejects logged
// with the raw input for debugging. Catches malformed scraper output
// (table-separator rows leaking through, "There are no open projects" sentinel
// rows, etc.) before it lands in opportunities table.
export const OpportunityCandidateSchema = z.object({
  externalId: z.string().min(1).max(500),
  title: z.string().min(4).max(500),
  description: z.string().max(20_000).optional(),
  // bidDueAt left as a free string — scrapers normalize via parseBidDueDate
  // (Phase 36E) but raw "MM/DD/YYYY" / ordinal-day formats are also valid input.
  bidDueAt: z.string().max(80).optional(),
  estimatedValueMin: z.number().nonnegative().max(1e10).optional(),
  estimatedValueMax: z.number().nonnegative().max(1e10).optional(),
  disciplines: z.array(DisciplineSchema).optional(),
  locationRaw: z.string().max(200).optional(),
  locationCity: z.string().max(80).optional(),
  locationState: z.string().min(2).max(8).optional(),
  gcName: z.string().max(200).optional(),
  ownerName: z.string().max(300).optional(),
  projectType: z.string().max(80).optional(),
  planSetUrl: z.string().url().optional(),
  sourcePageUrl: z.string().url(),
  // Phase 36K — per-opp detail page (separate from listing).
  detailUrl: z.string().url().optional(),
  // Phase 36B — solicitation / RFP / bid number for cross-source dedup.
  solicitationNumber: z.string().max(80).optional(),
  // R2 audit pass-through.
  rawContent: z.unknown().optional(),
});
export type OpportunityCandidate = z.infer<typeof OpportunityCandidateSchema>;
